import React from 'react';
import { t, getLang } from './i18n';
import { openStatus, guessTimezone, nowPartsInTz, oraripreghieraNascosti } from './api';

const COLORS = {
  primary: '#1a4d8f',
  bg: '#f7f8fa',
  text: '#1f2937',
  muted: '#6b7280',
  border: '#e5e7eb',
  danger: '#dc2626',
  success: '#059669',
};

const DAY_LABEL = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  daily: 'Every day',
  weekday: 'Weekdays',
};

const DAY_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'daily', 'weekday'];

// Idiomatic Hebrew short day forms (Sunday = א׳ … Friday = ו׳, Saturday = שבת);
// the English `.substring(0,3)` abbreviation ("Sun") makes no sense for Hebrew.
const HE_DAY_SHORT = { sun: 'א׳', mon: 'ב׳', tue: 'ג׳', wed: 'ד׳', thu: 'ה׳', fri: 'ו׳', sat: 'שבת', daily: 'כל יום', weekday: 'חול' };
const dayShort = (d) => {
  if (getLang() === 'he') return HE_DAY_SHORT[d] || t(DAY_LABEL[d] || d);
  const label = DAY_LABEL[d];
  if (!label) return d;
  return label.length >= 6 ? label : label.substring(0, 3);
};

function formatDays(days) {
  if (!days || days.length === 0) return '';
  if (days.includes('daily')) return t('Every day');
  // Legacy data may still carry 'weekday' as a single token — render it as
  // the natural "Mon – Fri" rather than the truncated "Wee" we used to show.
  if (days.length === 1 && days[0] === 'weekday') return t('Mon – Fri');
  if (days.length === 7) return t('Every day');
  // Check if it's a contiguous range
  const sorted = [...days].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
  // Detect Mon-Fri (weekdays without sat/sun)
  const weekdays = ['mon', 'tue', 'wed', 'thu', 'fri'];
  if (sorted.length === 5 && weekdays.every((d) => sorted.includes(d))) return t('Mon – Fri');
  // Detect contiguous range
  const indices = sorted.map((d) => DAY_ORDER.indexOf(d));
  const isContiguous = indices.every((idx, i) => i === 0 || idx === indices[i - 1] + 1);
  if (isContiguous && sorted.length >= 3) {
    return `${dayShort(sorted[0])} – ${dayShort(sorted[sorted.length - 1])}`;
  }
  // Per-day labels — localized short forms (Sun/Mon/… or א׳/ב׳/…), with full
  // labels for longer tokens so "Weekdays" doesn't truncate to gibberish.
  return sorted.map((d) => dayShort(d)).join(', ');
}

function formatTime12h(time) {
  if (time === 'shabbat_end') return t('End of Shabbat');
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return time;
  let [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m.toString().padStart(2, '0')} ${period}`;
}

// minutes-from-midnight (may be ≥1440 for an overnight close) → "5:00 PM".
function fmtMinutes(min) {
  const v = ((min % 1440) + 1440) % 1440;
  let h = Math.floor(v / 60); const m = String(v % 60).padStart(2, '0');
  const period = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12; else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
}

// The "Open now / Closed" pill for the Opening Hours header.
function OpenNowBadge({ status }) {
  const whenLabel = (o) => o.dayOffset === 0 ? t('today') : o.dayOffset === 1 ? t('tomorrow') : (getLang() === 'he' ? t(DAY_LABEL[o.dayCode] || o.dayCode) : t(DAY_LABEL[o.dayCode] || o.dayCode));
  let bg, color, icon, label, extra = '';
  if (status.open && status.closingSoon) {
    bg = '#fef3c7'; color = '#92400e'; icon = '🟠'; label = t('Closing soon');
    if (status.closesAt != null) extra = ` · ${fmtMinutes(status.closesAt)}`;
  } else if (status.open) {
    bg = '#dcfce7'; color = '#166534'; icon = '🟢'; label = t('Open now');
    if (status.closesAt != null) extra = ` · ${t('until')} ${fmtMinutes(status.closesAt)}`;
  } else if (status.seasonClosed) {
    // "Closed for the season" answers a different question than "Closed" — it
    // tells a visitor not to come back tomorrow. Only name a reopening date when
    // one is actually on file; inventing one would be worse than saying nothing.
    bg = '#e0e7ff'; color = '#3730a3'; icon = '🗓️'; label = t('Closed for the season');
    if (status.reopensOn) {
      const d = new Date(`${status.reopensOn}T00:00:00`);
      extra = ` · ${t('reopens')} ${Number.isNaN(d.getTime()) ? status.reopensOn : d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}`;
    }
  } else {
    bg = '#fee2e2'; color = '#991b1b'; icon = '🔴'; label = t('Closed');
    if (status.opensNext) extra = ` · ${t('opens')} ${whenLabel(status.opensNext)} ${fmtMinutes(status.opensNext.minutes)}`;
  }
  return (
    <span style={{ fontSize: 12, fontWeight: 700, background: bg, color, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {icon} {label}<span style={{ fontWeight: 500 }}>{extra}</span>
    </span>
  );
}

export default function OpeningHours({ establishment }) {
  const oh = establishment.opening_hours;

  // Don't show this component if there's structured prayer times (we use PrayerTimes for synagogues)
  // Se gli orari di preghiera sono nascosti questo riquadro deve tornare,
  // altrimenti la scheda resta senza nessun orario.
  if (establishment.prayer_times && !oraripreghieraNascosti(establishment)) return null;

  // If we have neither structured hours nor raw text, show nothing
  const hasStructured = oh && oh.schedule?.length > 0;
  const hasRaw = establishment.hours || establishment.open_days;
  if (!hasStructured && !hasRaw) return null;

  const status = openStatus(establishment); // null unless there are real open/close ranges

  // Seasonal / month-specific rows: mark the one that applies to the current
  // month (in the place's timezone) and dim the others.
  const hasSeasonal = hasStructured && oh.schedule.some((e) => e.season);
  const nowMonth = hasSeasonal ? (() => {
    const c = establishment.coordinates;
    const lat = c && typeof c.y === 'number' ? c.y : null;
    const lng = c && typeof c.x === 'number' ? c.x : null;
    return nowPartsInTz(guessTimezone(establishment.country, lat, lng)).month;
  })() : null;
  const seasonNow = (e) => !Array.isArray(e.months) || (nowMonth != null && e.months.includes(nowMonth));

  return (
    <div style={{ background: 'white', padding: 24, borderRadius: 12, marginTop: 20, border: `1px solid ${COLORS.border}` }}>
      <h3 style={{ marginTop: 0, color: COLORS.primary, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        🕐 {t('Opening Hours')}
        {status && <OpenNowBadge status={status} />}
      </h3>

      {establishment.open_days && (
        <div style={{ marginBottom: 12, fontSize: 14, color: COLORS.muted }}>
          <strong style={{ color: COLORS.text }}>{t('Open:')}</strong> {establishment.open_days}
        </div>
      )}

      {hasStructured ? (
        <div>
          {oh.schedule.map((entry, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: idx < oh.schedule.length - 1 ? `1px dashed ${COLORS.border}` : 'none', opacity: entry.season && !seasonNow(entry) ? 0.5 : 1 }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: COLORS.text }}>
                {formatDays(entry.days)}
                {entry.season && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: seasonNow(entry) ? '#dcfce7' : COLORS.bg, color: seasonNow(entry) ? '#166534' : COLORS.muted }}>
                    {entry.season}{seasonNow(entry) ? ` · ${t('now')}` : ''}
                  </span>
                )}
              </span>
              <span style={{ fontSize: 14, color: entry.closed ? COLORS.danger : COLORS.text, textAlign: 'right' }}>
                {entry.closed ? (
                  t('Closed')
                ) : entry.ranges && entry.ranges.length > 0 ? (
                  <>
                    {entry.ranges.map((r, i) => (
                      <span key={i}>
                        {formatTime12h(r.open)} – {formatTime12h(r.close)}
                        {i < entry.ranges.length - 1 && <span style={{ color: COLORS.muted }}> · </span>}
                      </span>
                    ))}
                    {entry.note && (
                      <span style={{ color: COLORS.muted, fontStyle: 'italic', marginLeft: 8, fontSize: 13 }}>
                        ({entry.note})
                      </span>
                    )}
                  </>
                ) : entry.note ? (
                  <span style={{ color: COLORS.muted, fontStyle: 'italic' }}>{entry.note}</span>
                ) : null}
              </span>
            </div>
          ))}
          {establishment.hours && (
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, color: COLORS.muted }}>
                {t('Original text from directory')}
              </summary>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: COLORS.muted, fontFamily: 'inherit', marginTop: 8, paddingLeft: 12, borderLeft: `3px solid ${COLORS.border}` }}>
                {establishment.hours}
              </pre>
            </details>
          )}
        </div>
      ) : (
        <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit', fontSize: 14, color: COLORS.text }}>
          {establishment.hours}
        </pre>
      )}
    </div>
  );
}
